<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url, minLength } from '@vuelidate/validators';
import wootConstants from 'dashboard/constants/globals';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { EXAMPLE_WEBHOOK_URL } = wootConstants;

const SUPPORTED_WEBHOOK_EVENTS = [
  'conversation_created',
  'conversation_status_changed',
  'conversation_updated',
  'message_created',
  'message_updated',
  'webwidget_triggered',
  'contact_created',
  'contact_updated',
  'conversation_typing_on',
  'conversation_typing_off',
  'sla_warning',
  'sla_breached',
];

export default {
  components: {
    NextButton,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isRegeneratingCustomHeaderToken: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      required: true,
    },
  },
  emits: ['submit', 'cancel', 'regenerateCustomHeaderToken'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    url: {
      required,
      minLength: minLength(7),
      url,
    },
    subscriptions: {
      required,
    },
  },
  data() {
    const { inboxEventsEnabled } = useConfig();
    return {
      url: this.value.url || '',
      name: this.value.name || '',
      subscriptions: this.value.subscriptions || [],
      secretVisible: false,
      customHeaderTokenVisible: false,
      customHeaderEnabled: this.value.custom_header_enabled || false,
      supportedWebhookEvents: inboxEventsEnabled
        ? [...SUPPORTED_WEBHOOK_EVENTS, 'inbox_updated']
        : SUPPORTED_WEBHOOK_EVENTS,
    };
  },
  computed: {
    hasSecret() {
      return !!this.value.secret;
    },
    hasCustomHeaderToken() {
      return !!this.value.custom_header_token;
    },
    webhookURLInputPlaceholder() {
      return this.$t(
        'INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.PLACEHOLDER',
        {
          webhookExampleURL: EXAMPLE_WEBHOOK_URL,
        }
      );
    },
    webhookNameInputPlaceholder() {
      return this.$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.PLACEHOLDER');
    },
  },
  methods: {
    onSubmit() {
      this.$emit('submit', {
        url: this.url,
        name: this.name,
        subscriptions: this.subscriptions,
        custom_header_enabled: this.customHeaderEnabled,
      });
    },
    async copySecret() {
      await copyTextToClipboard(this.value.secret);
      useAlert(this.$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY_SUCCESS'));
    },
    async copyCustomHeaderToken() {
      await copyTextToClipboard(this.value.custom_header_token);
      useAlert(
        this.$t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.COPY_SUCCESS')
      );
    },
    getI18nKey,
  },
};
</script>

<template>
  <form class="flex flex-col w-full" @submit.prevent="onSubmit">
    <div class="w-full">
      <label :class="{ error: v$.url.$error }">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.LABEL') }}
        <input
          v-model="url"
          type="text"
          name="url"
          :placeholder="webhookURLInputPlaceholder"
          @input="v$.url.$touch"
        />
        <span v-if="v$.url.$error" class="message">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.ERROR') }}
        </span>
      </label>
      <label>
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.LABEL') }}
        <input
          v-model="name"
          type="text"
          name="name"
          :placeholder="webhookNameInputPlaceholder"
        />
      </label>
      <label v-if="hasSecret" class="mb-4">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.LABEL') }}
        <div class="flex items-center gap-2">
          <input
            :value="
              secretVisible ? value.secret : '••••••••••••••••••••••••••••••••'
            "
            type="text"
            readonly
            class="!mb-0 font-mono"
          />
          <NextButton
            v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.TOGGLE')"
            type="button"
            :icon="secretVisible ? 'i-lucide-eye-off' : 'i-lucide-eye'"
            slate
            faded
            @click="secretVisible = !secretVisible"
          />
          <NextButton
            v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY')"
            type="button"
            icon="i-lucide-copy"
            slate
            faded
            @click="copySecret"
          />
        </div>
      </label>
      <div class="mb-4">
        <label class="flex items-center gap-2 mb-1">
          <input
            v-model="customHeaderEnabled"
            type="checkbox"
            name="customHeaderEnabled"
          />
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.ENABLED') }}
        </label>
        <p class="text-sm text-n-slate-11 mb-2">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.DESCRIPTION') }}
        </p>
        <p class="text-sm font-mono text-n-slate-12 mb-2">
          {{ value.custom_header_name || 'X-Chatwoot-Webhook-Token' }}
        </p>
        <label v-if="hasCustomHeaderToken">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.TOKEN') }}
          <div class="flex items-center gap-2">
            <input
              :value="
                customHeaderTokenVisible
                  ? value.custom_header_token
                  : '••••••••••••••••••••••••••••••••'
              "
              type="text"
              readonly
              class="!mb-0 font-mono"
            />
            <NextButton
              v-tooltip.top="
                $t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.TOGGLE')
              "
              type="button"
              :icon="
                customHeaderTokenVisible ? 'i-lucide-eye-off' : 'i-lucide-eye'
              "
              slate
              faded
              @click="customHeaderTokenVisible = !customHeaderTokenVisible"
            />
            <NextButton
              v-tooltip.top="
                $t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.COPY')
              "
              type="button"
              icon="i-lucide-copy"
              slate
              faded
              @click="copyCustomHeaderToken"
            />
            <NextButton
              type="button"
              slate
              faded
              :is-loading="isRegeneratingCustomHeaderToken"
              :label="
                $t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.REGENERATE')
              "
              @click="$emit('regenerateCustomHeaderToken')"
            />
          </div>
        </label>
        <p v-else class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.CUSTOM_HEADER.TOKEN_ON_SAVE') }}
        </p>
      </div>
      <label :class="{ error: v$.url.$error }" class="mb-2">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.LABEL') }}
      </label>
      <div class="flex flex-col gap-2.5 mb-4">
        <div
          v-for="event in supportedWebhookEvents"
          :key="event"
          class="flex items-center"
        >
          <input
            :id="event"
            v-model="subscriptions"
            type="checkbox"
            :value="event"
            name="subscriptions"
            class="mr-2"
          />
          <label :for="event" class="text-sm">
            {{
              `${$t(
                getI18nKey(
                  'INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS',
                  event
                )
              )} (${event})`
            }}
          </label>
        </div>
      </div>
    </div>

    <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.CANCEL')"
        @click.prevent="$emit('cancel')"
      />
      <NextButton
        type="submit"
        :disabled="v$.$invalid || isSubmitting"
        :is-loading="isSubmitting"
        :label="submitLabel"
      />
    </div>
  </form>
</template>
